import { ReloadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Divider, List, Space, Typography } from "antd";
import {
  checkLateArrivingInfo,
  fixInventoryControlPeriodWithLateArrivingInfo,
  ResponseLateArrivingInfo,
} from "api/generated";
import { handleAxiosError } from "common/helpers/generalUtils";
import dayjs from "dayjs";
import { useClient } from "hooks/useClient";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
dayjs.locale("es");

interface LateArrivingInformationAlertProps {
  onReload: () => void;
  isLoading: boolean;
  startDate: string;
  endDate: string;
  distributionPointIds: string[];
}

function LateArrivingInformationAlert({
  onReload,
  isLoading,
  startDate,
  endDate,
  distributionPointIds,
}: LateArrivingInformationAlertProps) {
  const { client } = useClient();
  // ESTADO PARA REGISTROS CON INFORMACION TARDIA
  const [invalidGics, setInvalidGics] = useState<ResponseLateArrivingInfo[]>(
    []
  );
  const fetchLateInfoControllerRef = useRef<AbortController | null>(null);
  const [loading, setLoading] = useState(false);

  const reloadLateInfo = useCallback(() => {
    setInvalidGics([]);
    if (distributionPointIds.length === 0) {
      return;
    }

    // Cancelar petición anterior si existe
    if (fetchLateInfoControllerRef.current) {
      fetchLateInfoControllerRef.current.abort();
    }
    // Crear nuevo AbortController para esta petición
    const controller = new AbortController();
    fetchLateInfoControllerRef.current = controller;

    checkLateArrivingInfo({
      client,
      body: {
        startDate,
        endDate,
        distributionPointIds,
      },
      signal: controller.signal,
    })
      .then(({ data }) => {
        setInvalidGics(data ?? []);
      })
      .catch((err) =>
        handleAxiosError(err, "Error buscando registros con informacion tardia")
      );
  }, [distributionPointIds, startDate, endDate, client]);

  useEffect(() => {
    reloadLateInfo();
  }, [reloadLateInfo]);

  function recalculateGicsWithLateArrivingInfo(
    distributionPointId: string,
    dateToReCalculate: string
  ) {
    setLoading(true);
    fixInventoryControlPeriodWithLateArrivingInfo({
      client,
      path: {
        distributionPointID: distributionPointId,
        startDate: dateToReCalculate,
      },
    })
      .catch((err) =>
        handleAxiosError(err, "Error buscando registros con informacion tardia")
      )
      .finally(() => {
        setLoading(false);
        onReload();
        reloadLateInfo();
      });
  }

  // Agrupar por punto de distribución
  const groupedByPoint: Record<string, ResponseLateArrivingInfo[]> =
    useMemo(() => {
      return invalidGics.reduce((acc, item) => {
        const dpName = item.distributionPoint?.name || "?";
        if (!acc[dpName]) {
          acc[dpName] = [];
        }
        acc[dpName].push(item);
        return acc;
      }, {} as Record<string, ResponseLateArrivingInfo[]>);
    }, [invalidGics]);

  return (
    <div>
      {invalidGics.length !== 0 && (
        <div>
          <Divider />
          <Alert
            type="error"
            showIcon
            message={
              <Space direction="vertical">
                <div>
                  <strong>Se detectó información tardía</strong>
                </div>
                <Typography.Text type="secondary">
                  Existen controles de existencia con información que llegó
                  después del periodo de cálculo. Esto afecta la precisión de
                  los reportes.
                </Typography.Text>
              </Space>
            }
            description={
              <div>
                <Typography.Title level={5}>
                  Períodos afectados ({invalidGics.length})
                </Typography.Title>

                {Object.entries(groupedByPoint).map(([dpName, items]) => (
                  <Card
                    key={dpName}
                    size="small"
                    title={dpName}
                    extra={
                      <Typography.Text type="secondary">
                        {items.length} período{items.length > 1 ? "s" : ""}
                      </Typography.Text>
                    }
                  >
                    <List
                      size="small"
                      dataSource={items}
                      renderItem={(item) => (
                        <List.Item
                          actions={[
                            <Button
                              key="recalculate"
                              type="link"
                              size="small"
                              icon={<ReloadOutlined />}
                              loading={loading || isLoading}
                              onClick={() => {
                                if (
                                  !item.distributionPoint?.id ||
                                  !item.firstDateWithLateArrivingInfo
                                ) {
                                  return;
                                }

                                recalculateGicsWithLateArrivingInfo(
                                  item.distributionPoint.id,
                                  item.firstDateWithLateArrivingInfo
                                );
                              }}
                            >
                              Recalcular
                            </Button>,
                          ]}
                        >
                          <List.Item.Meta
                            title={
                              <Typography.Text strong>
                                {dayjs(item.firstDateWithLateArrivingInfo)
                                  .format("MMMM YYYY")
                                  .toUpperCase()}
                              </Typography.Text>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </Card>
                ))}
              </div>
            }
          />
        </div>
      )}
    </div>
  );
}

export default LateArrivingInformationAlert;
